import {
  collection,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../../firebase.js';
import { resolveUserFullName } from '../utils/userUtils.js';

/**
 * Human-readable field labels across CRM modules
 */
export const FIELD_LABELS = {
  // Contacts
  first_name: 'First Name',
  last_name: 'Last Name',
  full_name: 'Full Name',
  id_number: 'ID / Passport Number',
  title: 'Job Title / Role',
  job_title: 'Job Title / Role',
  email: 'Email Address',
  phone_work: 'Office Phone',
  phone_mobile: 'Cellphone Number',
  whatsapp_number: 'WhatsApp Number',
  status: 'Status',
  is_primary_contact: 'Primary Contact',
  is_billing_contact: 'Billing Contact',
  is_technical_contact: 'Technical Contact',

  // Products & Services
  service_type: 'Service Type',
  product_name: 'Product / Service Name',
  quantity: 'Quantity',
  unit_price_ex_vat: 'Unit Price (Ex VAT)',
  unit_cost_ex_vat: 'Cost Price (Ex VAT)',
  total_price_ex_vat: 'Total Price (Ex VAT)',
  billing_frequency: 'Billing Frequency',
  contract_term: 'Contract Term',
  service_provider: 'Service Provider',
  internet_service_provider: 'ISP',
  date_activated: 'Date Activated',
  fibre_connection_type: 'Fibre Connection Type',
  lte_connection_type: 'LTE Connection Type',
  lte_cellular_number: 'LTE Cellular Number',
  sim_serial_number: 'SIM Serial Number',
  router_make_model: 'Router Model',
  router_serial_number: 'Router Serial Number',
  router_mac_address: 'Router MAC Address',
  wifi_username: 'Wi-Fi SSID',
  wifi_password: 'Wi-Fi Password',
  ip_phone_make_model: 'IP Phone Model',
  ip_phone_serial_number: 'IP Phone Serial Number',
  ip_phone_mac_address: 'IP Phone MAC Address',
  pabx_ip: 'PABX IP Address',
  extension_numbers: 'Extension Numbers',
  outbound_route: 'Outbound Route',
  inbound_route: 'Inbound Route',
  trunk_name: 'Trunk Name',
  trunk_auth_name: 'Trunk Auth Name',
  ported_number: 'Ported Number',
  ported_to: 'Ported To',
  pppoe_username: 'PPPoE Username',
  cellular_phone_model: 'Cellular Model',
  cellular_package_type: 'Cellular Package',
  comments: 'Comments / Notes',
  description: 'Description',

  // Account Profile & Info
  name: 'Trading As Name',
  registered_business_name: 'Registered Business Name',
  account_number: 'Account Number',
  account_type: 'Account Type',
  user_type: 'User Type',
  phone_office: 'Office Phone Number',
  phone_alternate: 'Alternate Phone',
  email1: 'Email Address',
  website: 'Official Website',
  official_website: 'Official Website',
  vat_number: 'GST / VAT Number',
  company_registration_number: 'Company Reg / ID #',
  billing_address_street: 'Street Address',
  billing_address_suburb: 'Suburb',
  billing_address_city: 'City',
  billing_address_state: 'Province / State',
  billing_address_postal_code: 'Postal Code',
  billing_address_country: 'Country',
  primary_contact_name: 'Primary Contact Name',
  primary_contact_title: 'Primary Contact Title',
  primary_contact_phone: 'Primary Contact Mobile',
  primary_contact_whatsapp: 'Primary Contact WhatsApp',
  primary_contact_email: 'Primary Contact Email',

  // Banking & Billing
  payment_method: 'Payment Method',
  debit_order: 'Debit Order Active',
  debit_order_date: 'Debit Order Date',
  CollectionDay: 'Collection Day',
  bank_name: 'Bank Name',
  bank_account_holder: 'Account Holder',
  bank_account_number: 'Bank Account Number',
  bank_branch_code: 'Branch Code',
  bank_account_type: 'Bank Account Type',
  billing_email: 'Billing Email',
  reseller_tier: 'Reseller Tier',

  // Relationships
  qbo_client_id: 'QuickBooks Client ID',
  qbo_client_name: 'QuickBooks Client Name',
  parent_account_name: 'Parent Account',

  // Cases & Activities & Comments
  subject: 'Subject',
  priority: 'Priority',
  category: 'Category',
  content: 'Comment Content',
  activity_type: 'Activity Type',
  due_date: 'Due Date'
};

const IGNORED_DIFF_KEYS = new Set([
  'id',
  'createdAt',
  'updatedAt',
  'created_at',
  'updated_at',
  'qbo_synced_at',
  'summary_counts',
  'user_type_c',
  'account_number_c',
  'debit_order_date_c',
  'debit_order_c',
  'service_state',
  'pabx_host',
  'sip_username',
  'sip_password',
  'wifi_ssid',
  'author_email',
  'author_name',
  'updatedBy',
  'createdBy',
  'is_telephony',
  'total_price_ex_vat'
]);

/**
 * Formats any raw field value into a clean display string
 */
export function formatChangeValue(val, fieldName = '') {
  if (val === undefined || val === null || val === '') return 'Empty';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (fieldName.includes('price') || fieldName.includes('cost') || fieldName === 'unitPrice' || fieldName === 'costPrice') {
    const num = Number(val);
    if (!isNaN(num)) return `R ${num.toFixed(2)}`;
  }
  if (typeof val === 'object') {
    if (val.seconds !== undefined) {
      return new Date(val.seconds * 1000).toISOString().split('T')[0];
    }
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val).trim() || 'Empty';
}

/**
 * Resolves the currently active user's full name and email
 */
export function getActiveActor(explicitUser = null, explicitEmail = null, explicitName = null) {
  const currentUser = explicitUser || auth?.currentUser || null;
  const email = (
    explicitEmail ||
    currentUser?.email ||
    'technician@abacustelecomm.co.za'
  ).toLowerCase().trim();
  const name = resolveUserFullName(explicitName || currentUser, email);
  return { userName: name, userEmail: email };
}

/**
 * Builds a concise, informative detail summary for a Product / Service item
 */
export function describeProductItem(item = {}) {
  if (!item) return 'Product / Service';
  const baseName = item.product_name || item.service_type || item.name || 'Product / Service';
  const details = [];

  if (item.trunk_name && !baseName.includes(item.trunk_name)) {
    details.push(`Trunk: ${item.trunk_name}`);
  } else if (item.trunk_auth_name && !baseName.includes(item.trunk_auth_name)) {
    details.push(`Auth: ${item.trunk_auth_name}`);
  }
  if (item.extension_numbers && !baseName.includes(item.extension_numbers)) {
    details.push(`Ext: ${item.extension_numbers}`);
  }
  if (item.pabx_ip) {
    details.push(`IP: ${item.pabx_ip}`);
  }
  if (item.router_make_model && !baseName.includes(item.router_make_model)) {
    details.push(item.router_make_model);
  }
  if (item.ip_phone_make_model && !baseName.includes(item.ip_phone_make_model)) {
    details.push(item.ip_phone_make_model);
  }
  if (item.serial_number || item.router_serial_number || item.ip_phone_serial_number) {
    details.push(`SN: ${item.serial_number || item.router_serial_number || item.ip_phone_serial_number}`);
  }
  if (item.mac_address || item.router_mac_address || item.ip_phone_mac_address) {
    details.push(`MAC: ${item.mac_address || item.router_mac_address || item.ip_phone_mac_address}`);
  }
  if (item.lte_cellular_number && !baseName.includes(item.lte_cellular_number)) {
    details.push(`LTE #: ${item.lte_cellular_number}`);
  }
  if (item.ported_number && !baseName.includes(item.ported_number)) {
    details.push(`Port: ${item.ported_number}`);
  }
  if (item.service_provider) {
    details.push(`Provider: ${item.service_provider}`);
  }
  const qty = Number(item.quantity) || 1;
  if (qty > 1) {
    details.push(`x${qty}`);
  }
  if (Number(item.unit_price_ex_vat) > 0) {
    details.push(`R ${Number(item.unit_price_ex_vat).toFixed(2)}`);
  }

  return details.length > 0 ? `${baseName} (${details.join(', ')})` : baseName;
}

/**
 * Determines which CRM section an account-level field belongs to
 */
export function resolveAccountFieldSection(fieldName = '') {
  const f = String(fieldName).toLowerCase();
  if (
    f.startsWith('bank_') ||
    f.includes('debit_order') ||
    f === 'payment_method' ||
    f === 'collectionday' ||
    f === 'billing_email' ||
    f === 'reseller_tier'
  ) {
    return 'Banking & Billing';
  }
  if (f.startsWith('qbo_')) {
    return 'Build Relationship';
  }
  if (f.startsWith('parent_account') || f === 'is_member_account') {
    return 'Member Accounts';
  }
  if (
    ['industry', 'annual_revenue', 'employees', 'ownership', 'ticker_symbol', 'rating', 'sic_code', 'lead_source', 'assigned_user_name', 'technical_notes'].includes(f)
  ) {
    return 'Additional Info';
  }
  return 'Profile & Info';
}

/**
 * Computes field-by-field diffs between an old object and updated fields
 */
export function computeFieldDiffs(oldObj = {}, newFields = {}) {
  const diffs = [];
  Object.keys(newFields || {}).forEach((key) => {
    if (IGNORED_DIFF_KEYS.has(key)) return;
    const newVal = newFields[key];
    if (newVal === undefined) return;
    const oldVal = oldObj ? oldObj[key] : undefined;

    // Skip if both are empty
    const normOld = oldVal === null || oldVal === undefined ? '' : String(oldVal).trim();
    const normNew = newVal === null || newVal === undefined ? '' : String(newVal).trim();
    if (normOld === normNew) return;

    // Skip numeric equivalence (e.g. 0 vs "0")
    if (normOld !== '' && normNew !== '' && !isNaN(Number(normOld)) && !isNaN(Number(normNew))) {
      if (Number(normOld) === Number(normNew)) return;
    }

    const label = FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    diffs.push({
      field: key,
      label,
      from: formatChangeValue(oldVal, key),
      to: formatChangeValue(newVal, key)
    });
  });
  return diffs;
}

/**
 * Records a Change Log entry to both `accounts/{accountId}/change_logs` and `crm_change_logs`
 */
export async function recordCrmChangeLog({
  accountId,
  accountName = '',
  section = 'CRM',
  action = 'Edited', // 'Added' | 'Edited' | 'Deleted'
  itemName = '',
  fieldLabel = '',
  previousValue = '',
  newValue = '',
  details = '',
  changes = [],
  user = null,
  userEmail = null,
  userName = null
}) {
  if (!accountId) return null;

  try {
    const actor = getActiveActor(user, userEmail, userName);
    const nowIso = new Date().toISOString();

    let summaryLine = '';
    if (action === 'Deleted') {
      summaryLine = `${section} -- Deleted -- ${itemName}${details ? ` (${details})` : ''} (${actor.userName})`;
    } else if (action === 'Added') {
      summaryLine = `${section} -- Added -- ${itemName}${details ? ` (${details})` : ''} (${actor.userName})`;
    } else {
      if (previousValue && newValue && !fieldLabel) {
        summaryLine = `${section} -- ${previousValue} ---> ${newValue} (${actor.userName})`;
      } else if (fieldLabel) {
        summaryLine = `${section} -- Edit -- ${itemName ? `${itemName} -- ` : ''}${fieldLabel} -- ${previousValue || 'Empty'} ---> ${newValue || 'Empty'} (${actor.userName})`;
      } else {
        summaryLine = `${section} -- Edit -- ${itemName}${details ? ` -- ${details}` : ''} (${actor.userName})`;
      }
    }

    const entry = {
      accountId: String(accountId),
      accountName: accountName || 'Account',
      section,
      action,
      itemName: itemName || '',
      fieldLabel: fieldLabel || '',
      previousValue: previousValue !== undefined && previousValue !== null ? String(previousValue) : '',
      newValue: newValue !== undefined && newValue !== null ? String(newValue) : '',
      details: details || '',
      changes: Array.isArray(changes) ? changes : [],
      summaryLine,
      userName: actor.userName,
      userEmail: actor.userEmail,
      timestampIso: nowIso,
      createdAt: serverTimestamp()
    };

    const subColRef = collection(db, 'accounts', String(accountId), 'change_logs');
    const globalColRef = collection(db, 'crm_change_logs');

    await Promise.allSettled([
      addDoc(subColRef, entry),
      addDoc(globalColRef, entry)
    ]);

    return entry;
  } catch (err) {
    console.warn('[changeLogService] Failed to record change log:', err);
    return null;
  }
}
